import { createBaseQueryWithReauth } from '@foundation-bb/foundation-auth';
import { env } from '@/env';
import type { AppDispatch } from '@/store/store';
import { rysAuth, clear, refreshToken } from '@/store/authSlice';

// Endpoints that must NOT trigger refresh-and-retry on 401.
// - refresh: would recurse.
// - requestEmailCode / verifyEmailCode: part of obtaining auth.
// - getAuthConfig / submitPitchDeck: public endpoints; 401 from these is a server bug, not token expiry.
const SKIP_REAUTH = [
  'refresh',
  'requestEmailCode',
  'verifyEmailCode',
  'getAuthConfig',
  'submitPitchDeck',
];

/**
 * Foundation's canonical reauth base query: sends the access token + cookies,
 * retries transient 5xx, and on 401 performs a single (single-flighted) refresh
 * then retries once; a still-failing 401 dispatches logout.
 */
export const baseQueryWithReauth = createBaseQueryWithReauth({
  baseUrl: env.api.baseUrl,
  selectToken: state => rysAuth.selectors.selectAccessToken(state as never),
  refreshAction: () => rysAuth.thunks.refreshToken(),
  logoutAction: () => clear(),
  skipReauthEndpoints: SKIP_REAUTH,
});

/**
 * Mid-session refresh used by the SSE stream (which bypasses RTK Query's base
 * query). Dispatches the Foundation refresh thunk and reports success so the
 * caller can reopen the stream with the new bearer.
 */
export async function doRefresh(dispatch: AppDispatch): Promise<boolean> {
  const res = await dispatch(refreshToken());
  return refreshToken.fulfilled.match(res) && !!res.payload;
}
